import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import Select from "react-select";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import {
  FaUserTie,
  FaCalendarAlt,
  FaDollarSign,
  FaExchangeAlt,
  FaClipboardCheck,
  FaCommentAlt,
} from "react-icons/fa";
import { IoMdArrowRoundBack } from "react-icons/io";
import { IoSave } from "react-icons/io5";

const SalaryUpdateChild = ({ expenseRecord }) => {
  dayjs.extend(utc);
  let navigate = useNavigate();
  const [{ errorLogApi, placeHolderText, user }] = useAtom(gbState);
  const [churchMembers, setChurchMembers] = useState(null);

  // Fixed normalize function to handle UTC dates properly
  const normalizeDate = (date) => {
    if (!date) return null;

    try {
      console.log("Incoming date:", date);
      console.log("Date type:", typeof date);

      // If it's an ISO string from database (contains 'Z' or 'T')
      if (
        typeof date === "string" &&
        (date.includes("T") || date.includes("Z"))
      ) {
        // Parse as UTC and return dayjs UTC object
        const parsed = dayjs.utc(date);
        console.log("Parsed UTC string:", parsed.format());
        return parsed.isValid() ? parsed : null;
      }

      // If it's already a dayjs object, convert to UTC
      if (date && typeof date === "object" && date.$isDayjsObject) {
        const utcDate = dayjs.utc(date.toISOString());
        console.log("Converted dayjs to UTC:", utcDate.format());
        return utcDate.isValid() ? utcDate : null;
      }

      // If it's a native Date object
      if (date instanceof Date) {
        const utcDate = dayjs.utc(date.toISOString());
        console.log("Converted Date to UTC:", utcDate.format());
        return utcDate.isValid() ? utcDate : null;
      }

      // Fallback: try parsing as UTC
      const parsed = dayjs.utc(date);
      console.log("Fallback UTC parse:", parsed.format());
      return parsed.isValid() ? parsed : null;
    } catch (error) {
      console.error("Error normalizing date:", error);
      return null;
    }
  };

  const [formData, setFormData] = useState({
    description: expenseRecord?.description,
    employee_id: expenseRecord?.employee_id?._id,
    date: normalizeDate(expenseRecord?.date),
    amount: expenseRecord?.amount,
    transfer_source: expenseRecord?.transfer_source,
    status: expenseRecord?.status,
  });

  //   Church Members
  useEffect(() => {
    let userToken = localStorage.getItem("token");
    const config = {
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/user/church/expense/${user.church_id}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios(config)
      .then(function (response) {
        const members = response.data.members;
        setChurchMembers(
          members.map((member) => ({
            value: member._id,
            label:
              member.first_name +
              " " +
              member.last_name +
              " (" +
              member.user_type +
              ")",
          }))
        );
      })
      .catch(function (error) {
        toast.error(error?.response?.data?.message);
        errorLogApi(error, "SalaryList", "/salary");
      });
  }, []);

  const handleChange = ({ currentTarget: input }) => {
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };

  const handleUpdateExpense = (e) => {
    e.preventDefault();
    let userToken = localStorage.getItem("token");

    // Prepare the data with proper UTC date formatting
    const dataToSend = {
      ...formData,
      // Convert the date to ISO string if it's a dayjs object
      date: formData.date
        ? formData.date.$isDayjsObject
          ? formData.date.toISOString()
          : formData.date
        : null,
    };

    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/salary/update/${expenseRecord?.id}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      data: dataToSend,
    };
    axios(config)
      .then(function (response) {
        toast.success("Salary updated Successfully.");
        navigate("/salaries");
      })
      .catch(function (error) {
        toast.error(error?.response?.data?.message);
        errorLogApi(error, "SalaryUpdateChild", "salary/update");
      });
  };

  const incomeSourceOptions = [
    { value: 1, label: "Cash" },
    { value: 2, label: "Cheque" },
    { value: 3, label: "Online Transfer" },
    { value: 4, label: "Demand Draft" },
  ];

  const paymentStatusOption = [
    { value: 1, label: "Pending" },
    { value: 2, label: "Settled" },
    { value: 3, label: "Declined" },
  ];

  return (
    <div className="flex justify-center py-8 w-full min-h-screen bg-gray-100">
      <div className="p-8 w-full max-w-2xl bg-white rounded-lg shadow-md">
        <h2 className="mb-6 text-2xl font-semibold text-gray-800">
          Update Salary
        </h2>
        <form onSubmit={(e) => handleUpdateExpense(e)} autoComplete="off">
          <div className="space-y-6">
            <div className="flex items-center">
              <FaUserTie className="mr-2 text-gray-400" />
              <Select
                name="employee_id"
                onChange={(selected) => {
                  setFormData({
                    ...formData,
                    employee_id: selected.value,
                  });
                }}
                options={churchMembers}
                placeholder={placeHolderText("Select Member")}
                className="w-full"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    "&:hover": { borderColor: "#cbd5e0" },
                  }),
                }}
                value={
                  formData?.employee_id &&
                  churchMembers?.find((m) => m.value === formData?.employee_id)
                }
              />
            </div>

            <div className="flex items-center">
              <FaCalendarAlt className="mr-2 text-gray-400" />
              <DatePicker
                className="w-full rounded-md border-gray-300"
                name="date"
                onChange={(date) => {
                  // Store as dayjs UTC object
                  if (date) {
                    const utcDate = dayjs.utc(date.format("YYYY-MM-DD"));
                    setFormData({
                      ...formData,
                      date: utcDate,
                    });
                  } else {
                    setFormData({
                      ...formData,
                      date: null,
                    });
                  }
                }}
                showToday
                value={formData?.date}
                format="MM-DD-YYYY"
              />
            </div>

            <div className="flex items-center">
              <FaDollarSign className="mr-2 text-gray-400" />
              <input
                className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                name="amount"
                type="number"
                required
                placeholder="Enter Amount"
                autoComplete="off"
                onChange={handleChange}
                value={formData?.amount}
              />
            </div>

            <div className="flex items-center">
              <FaExchangeAlt className="mr-2 text-gray-400" />
              <Select
                name="transfer_source"
                onChange={(selected) => {
                  setFormData({
                    ...formData,
                    transfer_source: selected.label,
                  });
                }}
                options={incomeSourceOptions}
                placeholder={placeHolderText("Select Source")}
                className="w-full"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    "&:hover": { borderColor: "#cbd5e0" },
                  }),
                }}
                value={
                  formData?.transfer_source &&
                  incomeSourceOptions?.find(
                    (i) => i.label === formData?.transfer_source
                  )
                }
              />
            </div>

            <div className="flex items-center">
              <FaClipboardCheck className="mr-2 text-gray-400" />
              <Select
                name="status"
                onChange={(selected) => {
                  setFormData({
                    ...formData,
                    status: selected.value,
                  });
                }}
                options={paymentStatusOption}
                placeholder={placeHolderText("Select Status")}
                className="w-full"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    "&:hover": { borderColor: "#cbd5e0" },
                  }),
                }}
                value={
                  formData?.status &&
                  paymentStatusOption.find(
                    (p) => p.value === parseInt(formData?.status)
                  )
                }
              />
            </div>

            <div className="flex items-start">
              <FaCommentAlt className="mt-3 mr-2 text-gray-400" />
              <textarea
                className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                name="description"
                rows={3}
                placeholder="Salary Description (If any)"
                autoComplete="off"
                onChange={handleChange}
                value={formData?.description}
              />
            </div>
          </div>

          <div className="flex justify-end mt-8 space-x-4">
            <button
              className="flex items-center px-4 py-2 text-gray-600 bg-gray-200 rounded-md transition duration-300 hover:bg-gray-300"
              onClick={() => navigate(-1)}
              type="button"
            >
              <IoMdArrowRoundBack className="mr-2" /> Back
            </button>
            <button
              className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-md transition duration-300 hover:bg-blue-700"
              type="submit"
            >
              <IoSave className="mr-2" /> Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalaryUpdateChild;
